import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useContext,
} from 'react';
import debounce from 'lodash.debounce';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TaskItem from '@tiptap/extension-task-item';
import TaskList from '@tiptap/extension-task-list';
import { ATTR_IGNORE_NODE } from '../../../../../hooks/use-key-nav-groups';
import WidgetIcon from '../../widget-library/WidgetIcon';
import BubbleMenu from './BubbleMenu';
import preview from './preview.svg';
import { useIsBrowserTabActive } from '@/hooks/use-is-browser-tab-active';
import { Icon } from '@monash/portal-react';
import { WidgetContext } from '@/components/providers/WidgetProvider';
import VersionHistory from './version-history-menu/VersionHistoryMenu';
import Preview from './preview/Preview';
import { calculateVersionArray } from './utils/calculate-version-array';
import c from './notes.module.scss';

const Notes = ({
  data,
  updateDataByObject,
  widgetId,
  escapeSettingActionsFocusRef,
}) => {
  const [content, setContent] = useState(data?.content);
  const [selectedVersionIndex, setSelectedVersionIndex] = useState(0);
  const browserTabIsActive = useIsBrowserTabActive();

  const willOverrideExistingVersion = useRef(false);
  const timeout = useRef(null);
  const isDataReady = useRef();
  const isPreviewing = selectedVersionIndex !== 0;

  const { editActiveWidget } = useContext(WidgetContext);

  // Set a timer, during which we will write to the latest version,
  // instead of creating a new version.
  const setOverrideTimer = () => {
    clearTimeout(timeout.current);

    timeout.current = setTimeout(() => {
      willOverrideExistingVersion.current = false;
    }, 60_000);
  };

  const editor = useEditor(
    {
      extensions: [
        StarterKit,
        Placeholder.configure({
          placeholder: 'Start typing...',
        }),
        Document,
        TaskList,
        TaskItem.configure({
          nested: false,
        }),
      ],
      editorProps: {
        attributes: {
          [ATTR_IGNORE_NODE]: widgetId,
          'aria-hidden': 'true',
          id: `widget-${widgetId}`,
        },
      },
      content,
      onUpdate: () => {
        isDataReady.current &&
          debouncedUpdateData(willOverrideExistingVersion.current);

        setOverrideTimer();
      },
    },
    [content, widgetId]
  );

  const debouncedUpdateData = useCallback(
    debounce((overrideExistingVersion) => {
      willOverrideExistingVersion.current = true;
      const noteContent = editor?.getJSON();
      const versionArray = calculateVersionArray({
        noteContent,
        currentVersions: data?.versions,
        overrideExistingVersion,
      });

      // Write to firestore
      updateDataByObject({
        updates: {
          content: noteContent,
          versions: versionArray,
        },
      });
    }, 1000),
    [updateDataByObject, editor, data?.versions]
  );

  const noteRef = useRef();

  // Prefills content when data arrives on initial load, but also
  // when data updates when multiple tabs are open
  useEffect(() => {
    if (data && (!isDataReady.current || !browserTabIsActive)) {
      setContent(data?.content);
      isDataReady.current = true;
    }
  }, [data]);

  return (
    <div className={c.notes} ref={noteRef}>
      {editActiveWidget === widgetId && (
        <VersionHistory
          versions={data?.versions}
          widgetId={widgetId}
          escapeFocusRef={escapeSettingActionsFocusRef}
          selectedVersionIndex={selectedVersionIndex}
          setSelectedVersionIndex={setSelectedVersionIndex}
          setContent={setContent}
          updateDataByObject={updateDataByObject}
          willOverrideExistingVersion={willOverrideExistingVersion}
          setOverrideTimer={setOverrideTimer}
        />
      )}

      {isPreviewing ? (
        <Preview
          version={data?.versions?.[selectedVersionIndex]}
          widgetId={widgetId}
        />
      ) : (
        <>
          <div className={c.widgetContent}>
            <EditorContent
              editor={editor}
              aria-labelledby={`widget-${widgetId}`}
            />
          </div>
          {editor ? <BubbleMenu editor={editor} /> : null}
        </>
      )}
    </div>
  );
};

const NotesModule = {
  component: Notes,
  name: 'Notes',
  icon: WidgetIcon.Notes,
  previewImage: preview,
  previewBackgroundColor: '#FEF9C3',
  description: 'Jot your thoughts down quickly whenever you need.',

  getContainerStyles: (data) => {
    const colourOption = data?.colour || 'yellow'; // Default to yellow
    const colourData = NotesModule.additionalOptions[0].options.find(
      (opt) => opt.id === colourOption
    );

    return {
      backgroundColor: colourData?.value,
      color: colourData?.contrast,
      border: colourData?.outline || 'none',
    };
  },

  additionalOptions: [
    {
      key: 'colour',
      name: 'Colour',
      editType: 'colour',
      default: 'yellow',
      options: [
        {
          id: 'yellow',
          value: '#FAEDCB',
          contrast: '#333333',
          outline: '#333333',
        },
        {
          id: 'green',
          value: '#C9E4DE',
          contrast: '#333333',
          outline: '#333333',
        },
        {
          id: 'blue',
          value: '#C6DEF1',
          contrast: '#333333',
          outline: '#333333',
        },
        {
          id: 'purple',
          value: '#DBCDF0',
          contrast: '#333333',
          outline: '#333333',
        },
        {
          id: 'pink',
          value: '#F2C6DE',
          contrast: '#333333',
          outline: '#333333',
        },
        {
          id: 'brown',
          value: '#F7D9C4',
          contrast: '#333333',
          outline: '#333333',
        },
        {
          id: 'white',
          value: '#FFFFFF',
          contrast: '#333333',
          border: 'var(--card-border-color)',
          outline: '#333333',
        },
      ],
    },
    {
      key: 'name',
      name: 'Name',
      editType: 'text',
      default: 'Notes',
    },
  ],
  additionalActions: [
    {
      action: 'editNotesVersions',
      icon: <Icon.History size={20} />,
      text: 'Show version history',
    },
  ],
};

export default NotesModule;
